import { Injectable } from '@angular/core'
import { RequestServices } from '@services/request.services'
import { BehaviorSubject, Observable } from 'rxjs'
import { ChatModel } from '@models/chat.model'
import { ChatReplyModel } from '@models/chat-reply.model'
import * as moment from 'moment'
import { map, switchMap, tap } from 'rxjs/operators'
import { ClientModel, getFullName } from '@models/client.model'
import { ChatFileModel } from '@models/chat-file.model'
import { ActivatedRoute } from '@angular/router'
import { NotificationService } from '@services/notification.service'
import { SharedServices } from '@services/shared.services'
import { SocketService } from '@services/socket.service'
import MainConfig from '../../config/main'
import { environment } from '../../environments/environment'
import { AuthService } from '../services/auth.services'

@Injectable()
export class ChatService {

    private readonly _reverseMode = true
    private readonly _chatList = new BehaviorSubject<ChatModel[]>([])
    private readonly _chat = new BehaviorSubject<ChatModel>(null)
    private _closeChat = new BehaviorSubject<ChatModel>(null)
    private readonly _chatReplies = new BehaviorSubject<ChatReplyModel[]>([])
    private readonly clientId
    private IdCommunication
    private readonly clinic
    private sender = 'Someone'

    readonly chatList$ = this._chatList.asObservable()
    readonly chat$ = this._chat.asObservable()
    readonly chatReplies$ = this._chatReplies.asObservable()
    readonly closeChat$ = this._closeChat.asObservable()

    constructor(private requestServices: RequestServices,
                private route: ActivatedRoute,
                private notificationService: NotificationService,
                private socketService: SocketService,
                private sharedService: SharedServices,
                private authData: AuthService
    ) {
        this.clientId = this.sharedService.getClientId()
        this.clinic = this.sharedService.getUser()
        this.sharedService.changeEmittedSource$.subscribe(success => {
            if (success && success[0]) {
                if (success[0].hasOwnProperty('firstName') && success[0].hasOwnProperty('familyName')) {
                    this.sender = success[0].firstName + success[0].familyName
                }
            }
        })
    }

    get chat(): ChatModel {
        return this._chat.getValue()
    }

    set chat(val: ChatModel) {
        this._chat.next(val)
    }

    get replies(): ChatReplyModel[] {
        return this._chatReplies.getValue()
    }

    set replies(val: ChatReplyModel[]) {
        this._chatReplies.next(val)
    }

    get chatList(): ChatModel[] {
        return this._chatList.getValue()
    }

    set chatList(val: ChatModel[]) {
        this._chatList.next(val)
    }

    public initList(chatList) {
        this.chatList = chatList
    }

    public getList() {
        const chatPath = {}
        const chatQuery = '{"clientId":"' + this.clientId + '"}'
        chatPath[chatQuery] = true
        const resourceData = {
            status: {
                clientCommunication: {
                    paths: chatPath,
                },
            },
        }

        this.requestServices.httpPostResource(resourceData).subscribe((result: any) => {
            const chatList = result.response.clientCommunication['paths'][chatQuery]
            this.initList(chatList.sort(function(a, b) {
                return (b['updateTime']) - (a['updateTime'])
            }))
        })
    }

    public getListWithCommunicationID(id) {
        const chatPath = {}
        const chatQuery = '{"clientId":"' + id + '"}'
        chatPath[chatQuery] = true
        this.IdCommunication = id
        const resourceData = {
            status: {
                clientCommunication: {
                    paths: chatPath,
                },
            },
        }

        this.requestServices.httpPostResource(resourceData).subscribe((result: any) => {
            const chatList = result.response.clientCommunication['paths'][chatQuery]
            this.initList(chatList.sort(function(a, b) {
                return (b['createTime']) - (a['createTime'])
            }))
        })
    }

    /**
     * addReply
     * @param content
     * @param isPatient
     */
    public addReply(content: string, isPatient = false) {
        const replyData: ChatModel = this.initReplyModel(isPatient, content)

        this.storeChat(replyData, isPatient)
        if (this._reverseMode) {
            this.chatList = [
                replyData,
                ...this.chatList,
            ]
        } else {
            this.chatList = [
                ...this.chatList,
                replyData,
            ]
        }
    }

    /**
     *
     * @param chatFile
     * @param isPatient
     */
    public addFile(chatFile: ChatFileModel, isPatient: boolean) {
        const replyData: ChatModel = this.initReplyModel(isPatient, '[Attach file] ' + chatFile.name)
        replyData.file = chatFile

        this.chatList = [
            replyData,
            ...this.chatList,
        ]
        chatFile.handlingUpload.subscribe((response) => {
            delete chatFile.handlingUpload
            chatFile._id = response.hash

            this.storeChat(replyData, isPatient)
        })
    }

    /**
     *
     * @param chatId
     * @param widthExtendInfo
     */
    public getChatInfo(chatId, widthExtendInfo = false): Observable<object> {
        const chatPath = {}
        const replyPath = {}
        const chatQuery = '{"_id":"' + chatId + '"}'
        const replyQuery = '{"communicationId":"' + chatId + '"}'
        chatPath[chatQuery] = true
        replyPath[replyQuery] = true
        const resourceData = {
            status: {
                clientCommunication: {
                    paths: chatPath,
                },
                clientCommunicationReply: {
                    paths: replyPath,
                },
            },
        }
        return this.requestServices.httpPostResource(resourceData).pipe(
            tap((responseData: any) => {
                let replies = responseData.response.clientCommunicationReply.paths[replyQuery]
                replies = this._reverseMode
                    ? replies.sort((a, b) => b.createTime - a.createTime)
                    : replies.sort((a, b) => a.createTime - b.createTime)
                this.replies = replies
                return responseData
            }),
            map((responseData: any) => responseData.response.clientCommunication.paths[chatQuery][0]),
            tap(chatInfo => {
                this.chat = chatInfo
                return chatInfo
            }),
            switchMap(chatInfo => this.getExtendInfo(chatInfo, widthExtendInfo)),
        )
    }

    openChat() {
    }

    close() {
        this._closeChat.next(this.chat)
    }

    /**
     *
     * @param selectedChatTags
     */
    public updateChatPrivateTag(selectedChatTags: any[]) {
        this.chat.privateTags = selectedChatTags

        this.updateChat({
            privateTags: selectedChatTags,
        })
    }

    /**
     *
     * @param communicationId
     * @param data
     */
    public updateReaction(communicationId: any, data: any) {
        const updateData = {
            patientReact: data.patientReact || 0,
            clinicReact: data.clinicReact || 0,
        }
        this.updateCommunication(communicationId, updateData)
    }

    /**
     *
     * @param replyId
     * @param data
     */
    public updateReplyReaction(replyId: any, data: any) {
        const updateData = {
            patientReact: data.patientReact || 0,
            clinicReact: data.clinicReact || 0,
        }
        this.updateReply(replyId, updateData)
    }

    /**
     * Get extend info of chat: client, reply, thread
     * @param chatInfo
     * @param widthExtendInfo
     */
    private getExtendInfo(chatInfo, widthExtendInfo) {
        if (widthExtendInfo) {
            const searchClient = {}
            const searchClientQuery = '{"_id":"' + chatInfo.clientId + '"}'
            searchClient[searchClientQuery] = true
            const resourceData = {
                status: {
                    client: {
                        paths: searchClient,
                    },
                },
            }
            return this.requestServices.httpPostResource(resourceData).pipe(
                map((responseData: any) => {
                    chatInfo.client = responseData.response.client.paths[searchClientQuery][0]
                    return chatInfo
                }),
                tap(chatInfo => {
                    this.chat = chatInfo
                    return chatInfo
                }),
            )
        } else {
            return new Observable(s => s.next(chatInfo))
        }
    }

    /**
     *
     * @param replyData
     * @param isPatient
     */
    private storeChat(replyData: ChatModel, isPatient = false) {
        replyData.communication = {
            type: this.chat.type,
            note: this.chat.note,
            images: this.chat.images,
            video: this.chat.video,
            pdfs: this.chat.pdfs,
            content: this.chat.content,
        }

        const requestPayload = {
            actions: [
                {
                    collection: ChatModel.COLLECTION_NAME,
                    type: 'create',
                    value: replyData,
                },


            ],
        }

        if (isPatient) {
            const chatUpdateData: any = { lastPatientCommunicationAt: moment().valueOf() }
            const actionUpdateClient = {
                collection: ClientModel.COLLECTION_NAME,
                type: 'update',
                id: this.clientId ? this.clientId : (replyData ? replyData.clientId : null),
                value: chatUpdateData,
            }
            requestPayload.actions.push(actionUpdateClient)
        }

        // this.chat.lastReply = replyData
        if (this.authData.getItem('user')) {
            const user = JSON.parse(this.authData.getItem('user'));
            const expires = parseInt((user.expiresInSeconds)) * 1000;
            const timeOut = parseInt(this.authData.getItem('timeout')) + expires;
            const timeNow = Date.now();
            if(timeOut <= timeNow){
                this.close()
            }
        }
        return this.requestServices.httpPostResource(requestPayload).subscribe((data: any) => {
                console.log('saved reply content')
                replyData._id = data.response.clientCommunication.updatedItem
                this.close()
                this.sendCommunicationNotification('client-replied', replyData)
            },
        )
    }

    /**
     *
     * @param saveData
     */
    private updateChat(saveData) {
        return this.updateCommunication(this.chat._id, saveData)
    }

    /**
     *
     * @param itemId
     * @param saveData
     */
    private updateCommunication(itemId, saveData) {
        const requestPayload = {
            actions: [
                {
                    collection: ChatModel.COLLECTION_NAME,
                    type: 'update',
                    id: itemId,
                    value: saveData,
                },
            ],
        }

        return this.requestServices.httpPostResource(requestPayload).subscribe(data => {
                this.sendCommunicationNotification('client-replied', saveData)
            },
        )
    }

    /**
     *
     * @param replyId
     * @param saveData
     */
    private updateReply(replyId, saveData) {
        const requestPayload = {
            actions: [
                {
                    collection: ChatReplyModel.COLLECTION_NAME,
                    type: 'update',
                    id: replyId,
                    value: saveData,
                },
            ],
        }

        return this.requestServices.httpPostResource(requestPayload).subscribe(data => {
                this.sendCommunicationNotification('client-replied', saveData)
            },
        )
    }

    /**
     *
     * @param isPatient
     * @param content
     */
    private initReplyModel(isPatient, content = ''): ChatModel {
        return {
            // createOId: mongoObjectId(),
            communicationId: this.chat._id,
            clientId: this.chat.clientId || this.clientId,
            clientName: this.chat.client ? getFullName(this.chat.client) : '',
            content: content,
            createTime: moment().valueOf(),
            updateTime: moment().valueOf(),
            isPatientCreate: isPatient,
        }
    }

    private sendCommunicationNotification(notificationType, data) {
        if (!window.navigator.userAgent.match(/Cordova/)) {
            if (!this.clientId) {
                this.notificationService.performPushNotification(this.IdCommunication)
            } else {
                this.notificationService.performPushNotification(this.IdCommunication)
            }
        } else {
            const payload = {
                type: notificationType,
                modalId: data.communicationId,
                clinic: this.clinic,
                sender: this.sender,
            }
            if (MainConfig.ENABLE_WEB_NOTIFICATION) {
                this.socketService.emitWebSocketEvent('communication notice', payload)
                this.socketService.performPushWebNotification({
                    ...payload,
                    url: `${ environment.baseUrl }/app/main/client/edit/${ data.communicationId }/communication?modalId=${ data.communicationId }`,
                })
            }
        }
    }

}



// WEBPACK FOOTER //
// ./src/app/services/chat.service.ts